import React, { useState, useEffect, useMemo, useCallback } from "react";

import {
  Divider,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { ptBR } from "date-fns/locale";

import {
  ConsumerMonitoringUnits,
  DashboardMonitoringValues_V2,
} from "libLegacy/api/models";
import { fetchDashboardMonitoringBrief_V2 } from "libLegacy/api/dashboard";

import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import {
  SelectorGranularities,
  SelectorGranularitiesProps,
} from "components/admin/SelectorGranularities";
import { findMonth, findWeek, findYear } from "logic/DateSelectors";
import { LocalSystemStatus } from "components/admin/LocalSystemStatus";
import {
  ChartProsumerMonitoring,
  ChartProsumerMonitoringProps,
} from "components/charts/ChartProsumerMonitoring";
import {
  formatMonitoringFetchingParams,
  UNIT_CONFIG,
} from "logic/SharedMonitoring";
import { useToken, useUserContext } from "contexts/UserContext";
import { AutomationSpace } from "api/automationTypes";
import { useSetError } from "contexts/ErrorContext";

const PAGE_TABS = [
  {
    label: "diário",
    value: "day",
  },
  {
    label: "semanal",
    value: "week",
  },
  {
    label: "mensal",
    value: "month",
  },
  {
    label: "anual",
    value: "year",
  },
];

const useStyles = makeStyles()((theme) => ({
  toggleButton: {
    "&&.MuiToggleButton-root": {
      borderRadius: "20px",
      border: `1px solid ${theme.palette.secondary.main}`,
      padding: theme.spacing(1, 2),
      textTransform: "none",
      width: "25%",
      minWidth: "25px",

      "& > span": {
        fontWeight: 500,
      },

      "&:not(:first-child)": {
        marginLeft: theme.spacing(2),
      },

      "&.Mui-selected": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,

        "& > span": {
          color: theme.palette.common.white,
        },
      },
    },
  },
  verticalSummary: {
    flex: 1,
    borderRadius: "12px",
    border: `1px solid ${theme.palette.secondary.light}`,
    display: "grid",
    gap: theme.spacing(2, 1),
    gridTemplateColumns: "1fr 2fr",
    gridTemplateRows: "repeat(3, 1fr)",
    padding: theme.spacing(1, 0),
  },
  iconPos: {
    gridColumn: "1 / span 1",
    gridRow: "span 1",
    justifySelf: "end",
    alignSelf: "center",
    maxWidth: "35px",
  },
  textPos: {
    gridColumn: "2 / span 1",
  },
  horizontalSummary: {
    border: `1px solid ${theme.palette.secondary.light}`,
    padding: theme.spacing(2),
    borderRadius: "20px",

    "& > hr": {
      margin: theme.spacing(1, 0),
    },
  },
}));

const UNIT_SUFFIX: Record<ConsumerMonitoringUnits, string> = {
  CO2e: "CO2E",
  kWh: "",
  money: "MONEY",
};

const CHART_KEYS = {
  Production: "Produção",
  Consumption: "Consumo",
  InjectedEnergyGraph: "Injetada",
  GridEnergyGraph: "Rede",
  InstantaneousPowerGraph: "Autoconsumo instantâneo",
};

const extractValue = (
  baseName: string,
  unit: string,
  data?: DashboardMonitoringValues_V2
): string => {
  const placeholder = " - ";
  const unitSuffix = UNIT_SUFFIX[unit];
  const unitFieldName = `${baseName}${unitSuffix}`;

  return data && data[unitFieldName]
    ? data[unitFieldName].toLocaleString(ptBR.code, UNIT_CONFIG[unit])
    : placeholder;
};

const TODAY = new Date();

export const ProsumerMonitoring = ({ history }) => {
  const token = useToken();
  const { classes } = useStyles();
  const [referenceDate, setReferenceDate] = useState<{
    granularity: SelectorGranularitiesProps["granularity"];
    start: Date;
    end?: Date;
  }>({ granularity: "day", start: TODAY });
  const [units, setUnits] = useState<ConsumerMonitoringUnits>("kWh");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<DashboardMonitoringValues_V2 | undefined>(
    undefined
  );
  const { currentSpace } = useUserContext();
  const setGlobalError = useSetError();

  const chartData: ChartProsumerMonitoringProps["data"] = useMemo(() => {
    const filteredData: ChartProsumerMonitoringProps["data"] = [];
    if (data) {
      const keys = Object.keys(data);
      keys.forEach((key) => {
        // THIS CHECKING IS VERY FRAGILE, FIX ASAP
        if (Object.keys(CHART_KEYS).includes(key)) {
          filteredData.push({ name: CHART_KEYS[key], data: data[key] });
        }
      });
    }
    return filteredData;
  }, [data]);

  useEffect(() => {
    const load = async () => {
      const { start, end, granularity } = referenceDate;
      setIsLoading(true);
      setData(undefined);
      try {
        const filter = formatMonitoringFetchingParams(
          start,
          end || start,
          granularity,
          currentSpace?.contractCode || "",
          currentSpace?.id || ""
        );

        const data = await fetchDashboardMonitoringBrief_V2(token, filter);
        setData(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    if (currentSpace && currentSpace.isProsumer) {
      load();
    }
  }, [referenceDate, token, currentSpace, setGlobalError]);

  const spaceHasMonitoring = useCallback((space: AutomationSpace): boolean => {
    return space.isProsumer || space.hasSmartMeter;
  }, []);

  const handleNoSpaceAvailable = useCallback((): void => {
    history.push("onboarding/no-monitoring-credentials");
  }, [history]);

  const handleTabChange = (event, newValue) => {
    let start, end;

    if (newValue === "day") {
      start = TODAY;
    }

    if (newValue === "week") {
      [start, end] = findWeek(TODAY);
    }

    if (newValue === "month") {
      [start, end] = findMonth(TODAY);
    }

    if (newValue === "year") {
      [start, end] = findYear(TODAY);
    }

    setReferenceDate({ start, end, granularity: newValue });
  };

  const handleDateChange = (start: Date, end?: Date) => {
    setReferenceDate((prevState) => ({ ...prevState, start, end }));
  };

  const handleUnitChange = (event, newUnit) => {
    setUnits(newUnit);
  };

  return (
    <PageStandard
      title="Monitoramento"
      subtitle="Veja o seu consumo de energia em tempo real"
      displaySelector={true}
      selectorFilter={spaceHasMonitoring}
      onNoSpaceAvailable={handleNoSpaceAvailable}
    >
      <BlockStandard hideDivider>
        <Tabs
          textColor="secondary"
          value={referenceDate.granularity}
          onChange={handleTabChange}
          sx={{ m: 0 }}
        >
          {PAGE_TABS.map(({ value, label }) => (
            <Tab key={value} value={value} label={label} />
          ))}
        </Tabs>
        <SelectorGranularities
          startDate={referenceDate.start}
          endDate={referenceDate.end}
          onDateChange={handleDateChange}
          granularity={referenceDate.granularity}
        />
      </BlockStandard>

      <BlockStandard hideDivider>
        <LocalSystemStatus
          isLoading={isLoading}
          status="NORMAL"
          lastRead={data?.LastTimeRegistered || 0}
          temperature={data?.Weather?.condition.temperature}
          iconId={data?.Weather?.weather[0].icon}
          iconAlt={data?.Weather?.weather[0].description}
        />
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid container item justifyContent="space-around" sx={{ flex: 1 }}>
          <ToggleButtonGroup
            value={units}
            onChange={handleUnitChange}
            exclusive
            sx={{ justifyContent: "space-around" }}
          >
            <ToggleButton className={classes.toggleButton} value="kWh">
              <Typography variant="caption">kWh</Typography>
            </ToggleButton>

            <ToggleButton className={classes.toggleButton} value="money">
              <Typography variant="caption">R$</Typography>
            </ToggleButton>

            <ToggleButton className={classes.toggleButton} value="CO2e">
              <Typography variant="caption">CO2e</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid item container gap={2} direction="row">
          <Grid
            item
            container
            direction="column"
            className={classes.verticalSummary}
          >
            <img
              src="/static/icons/icon-solar-panel.svg"
              alt="Ilustração de um painel solar"
              className={classes.iconPos}
            />
            <Grid className={classes.textPos}>
              <Typography
                paragraph
                sx={{ m: 0 }}
                variant="body2"
                color="primary"
              >
                Produção
              </Typography>
              <Typography variant="body2" component="span">
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  extractValue("ProductionEnergy", units, data)
                )}
              </Typography>
              {!isLoading && units !== "money" && (
                <Typography variant="caption" component="span">
                  {` ${units}`}
                </Typography>
              )}
            </Grid>
            <img
              src="/static/icons/icon-injected.svg"
              alt="Ilustração de uma torre de distribuição de energia"
              className={classes.iconPos}
            />
            <Grid className={classes.textPos}>
              <Typography paragraph sx={{ m: 0 }} variant="caption">
                Injetado
              </Typography>
              <Typography variant="body2" component="span">
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  extractValue("InjectedEnergy", units, data)
                )}
              </Typography>
              {!isLoading && units !== "money" && (
                <Typography variant="caption" component="span">
                  {` ${units}`}
                </Typography>
              )}
            </Grid>
            <img
              src="/static/icons/icon-lamp.svg"
              alt="Ilustração de uma lâmpada"
              className={classes.iconPos}
            />
            <Grid className={classes.textPos}>
              <Typography paragraph sx={{ m: 0 }} variant="caption">
                Créditos
              </Typography>
              <Typography variant="body2" component="span">
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  extractValue("CreditEnergy", units, data)
                )}
              </Typography>
              {!isLoading && units !== "money" && (
                <Typography variant="caption" component="span">
                  {` ${units}`}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid
            item
            container
            direction="column"
            className={classes.verticalSummary}
          >
            <img
              src="/static/icons/icon-house-with-solar-system.svg"
              alt="Ilustração de uma casa com um sistema de paineis solares no teto"
              className={classes.iconPos}
            />
            <Grid className={classes.textPos}>
              <Typography
                paragraph
                sx={{ m: 0 }}
                variant="body2"
                color="primary"
              >
                Consumo
              </Typography>
              <Typography variant="body2" component="span">
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  extractValue("ConsumptionEnergy", units, data)
                )}
              </Typography>
              {!isLoading && units !== "money" && (
                <Typography variant="caption" component="span">
                  {` ${units}`}
                </Typography>
              )}
            </Grid>
            <img
              src="/static/icons/icon-smart-meter.svg"
              alt="Ilustração de um smartmeter"
              className={classes.iconPos}
            />
            <Grid className={classes.textPos}>
              <Typography paragraph sx={{ m: 0 }} variant="caption">
                Instantâneo
              </Typography>
              <Typography variant="body2" component="span">
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  extractValue("InstantaneousPower", units, data)
                )}
              </Typography>
              {!isLoading && units !== "money" && (
                <Typography variant="caption" component="span">
                  {` ${units}`}
                </Typography>
              )}
            </Grid>
            <img
              src="/static/icons/icon-injected.svg"
              alt="Ilustração de um smartmeter"
              className={classes.iconPos}
            />
            <Grid className={classes.textPos}>
              <Typography paragraph sx={{ m: 0 }} variant="caption">
                Rede
              </Typography>
              <Typography variant="body2" component="span">
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  extractValue("GridEnergy", units, data)
                )}
              </Typography>
              {!isLoading && units !== "money" && (
                <Typography variant="caption" component="span">
                  {` ${units}`}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <ChartProsumerMonitoring
          isLoading={isLoading}
          data={chartData}
          granularity={referenceDate.granularity}
          unit={units}
        />
      </BlockStandard>

      <BlockStandard>
        <Grid
          container
          item
          direction="column"
          className={classes.horizontalSummary}
        >
          <Grid container item direction="row" justifyContent="space-between">
            <Typography variant="body2">Desempenho da geração</Typography>
            <Typography variant="body2">
              {data?.Performance.toLocaleString(ptBR.code, {
                style: "percent",
              })}
            </Typography>
          </Grid>
          <Divider />
          <Grid container item direction="row" justifyContent="space-between">
            <Typography variant="caption">Estimada mensal</Typography>
            <Typography variant="caption">
              {isLoading ? (
                <Skeleton width={30} />
              ) : (
                `${data?.MonthlyEstimatedEnergyKwh.toLocaleString(
                  ptBR.code,
                  UNIT_CONFIG[units]
                )} kWh`
              )}
            </Typography>
          </Grid>
          <Divider />
          <Grid container item direction="row" justifyContent="space-between">
            <Typography variant="caption">Energia estimada anual</Typography>
            <Typography variant="caption">
              {isLoading ? (
                <Skeleton width={30} />
              ) : (
                `${data?.AnnualEstimatedEnergyKwh.toLocaleString(
                  ptBR.code,
                  UNIT_CONFIG[units]
                )} kWh`
              )}
            </Typography>
          </Grid>
        </Grid>
      </BlockStandard>
    </PageStandard>
  );
};
